import { useElements } from '@stripe/react-stripe-js';
import { BillingInfoType } from '@sdk/account/api/AccountApi';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { extendBillingInfo } from '@sdk/account/utils';
import useSlingApi from '@sdk/api/useSlingApi';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';

import { useStripeUtils } from '@web/account/hooks/stripeUtils.hooks';
import { usePurchaseContext } from '@web/account/purchase/PurchaseProvider/PurchaseProvider';
import { getIsYearlyPlan } from '@web/account/utils';

export const usePlanDetails = ({
  postSubscriptionPurchase,
  isShopifyBilling,
  currentUser,
}: {
  postSubscriptionPurchase: (subscription: BillingInfoType) => Promise<void>;
  isShopifyBilling: boolean;
  currentUser: AuthUserType;
}) => {
  const api = useSlingApi();
  const { completeStripePayment } = useStripeUtils();
  const elements = useElements();
  const { billingAddress, subscriptionLevel, subscriptionBillingCycle } = usePurchaseContext();
  const isYearlyPlan = getIsYearlyPlan(subscriptionBillingCycle);

  const { showErrorToast } = useToast();

  const purchaseWithShopify = async (): Promise<BillingInfoType> => {
    const { data } = await api.account.purchaseWithShopify({
      subscriptionLevel,
      billingDetails: currentUser.org.name,
    });

    return data;
  };

  const purchaseWithStripe = async (): Promise<BillingInfoType> => {
    const payload = {
      subscriptionLevel,
      isYearlyPlan,
      billingDetails: currentUser.org.name,
      supportSca: true,
      billingAddress,
    };

    const { error: elementsFormError } = await elements.submit();
    if (elementsFormError) {
      throw elementsFormError.message;
    }

    const { data: purchaseResult } = await api.account.purchaseSubscription(payload);
    const { error } = await completeStripePayment(purchaseResult, elements);

    if (error) {
      throw error.message;
    }

    const { data: subscription } = await api.account.getBillingInfo();

    return {
      ...subscription,
      subscriptionLevel,
    };
  };

  const completePurchase = async () => {
    try {
      postSubscriptionPurchase(
        extendBillingInfo(await (isShopifyBilling ? purchaseWithShopify() : purchaseWithStripe()))
      );
    } catch (e) {
      showErrorToast(getErrorForToast(e));

      throw e;
    }
  };

  return {
    completePurchase,
    subscriptionLevel,
    isYearlyPlan,
  };
};
